import en from "./locales/en/index";
import so from "./locales/so/index";

/**
 * @pharmacy/i18n — every user-facing string in the system.
 *
 * A typed dictionary rather than a translation library, for one reason: Somali
 * is declared as `typeof en`, so a key added in English and forgotten in Somali
 * stops the build. A string-keyed lookup would fall back to English instead, and
 * nobody would notice until a pharmacist did.
 *
 * The cost is that plurals and interpolation are hand-written per key, as
 * functions. At two locales that is a page of work; it also means every string
 * is checked by the compiler at its call site, with the right argument types.
 */

export type Locale = "so" | "en";

/** The shape every catalogue must satisfy. English defines it. */
export type Messages = typeof en;

/** Somali first: it is the default, and the market the product is built for. */
export const CATALOGUES: Record<Locale, Messages> = { so, en };

export const DEFAULT_LOCALE: Locale = "so";

export function messagesFor(locale: Locale): Messages {
  return CATALOGUES[locale];
}

/**
 * Narrow whatever the device reports to a locale we actually have.
 *
 * `expo-localization` returns tags like "so-SO", "en-GB" or occasionally
 * something unexpected, so this takes the language subtag and falls back rather
 * than trusting it. A phone set to Arabic should get Somali, not a crash.
 */
export function resolveLocale(tag: string | null | undefined): Locale {
  const language = (tag ?? "").toLowerCase().split(/[-_]/)[0];
  return language === "en" ? "en" : DEFAULT_LOCALE;
}

/**
 * Turn a server error code into something a person can act on.
 *
 * The API returns a stable `code` and an English `message`. The code is what
 * the client translates; the server's own message is the fallback for anything
 * not in the catalogue, which keeps a new server code readable rather than
 * silently blank while the translation catches up.
 *
 * `arg` carries the one number some messages need — minutes left on a lock, a
 * minimum length. It is ignored by the keys that take no argument.
 */
export function errorMessage(
  messages: Messages,
  code: string | undefined,
  fallback?: string,
  arg?: number,
): string {
  const entry = (messages.errors as Record<string, unknown>)[code ?? ""];

  if (typeof entry === "function") {
    return (entry as (n: number) => string)(arg ?? 0);
  }
  if (typeof entry === "string") return entry;

  return fallback?.trim() ? fallback : messages.errors.unknown;
}

export { shopWordsFor, withDukaanWords, type ShopWords } from "./dukaan";
